import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { JgsSearchForm } from "../JgsLinkBanner";
import { trackJgsProfileSkipped } from "@/analytics/analytics";
import { ChoiceCard, OnboardingQuestion } from "./OnboardingQuestion";
import type { OnboardingStepProps } from "./types";

/** Step 2 — branch into JGS search or move on when the player is not ranked. */
export default function JgsLinkStep({
  profile,
  onValidityChange,
  registerSave,
  onLinkSuccess,
  onStepComplete,
}: OnboardingStepProps) {
  useEffect(() => {
    registerSave(null);
    onValidityChange(false);
    return () => registerSave(null);
  }, [registerSave, onValidityChange]);

  const [phase, setPhase] = useState<"choice" | "search">("choice");

  const handleLink = async (profileId: string) => {
    await onLinkSuccess(profileId);
    await onStepComplete("linked");
  };

  const handleSkip = () => {
    trackJgsProfileSkipped({ source: "onboarding" });
    onStepComplete("skipped");
  };

  if (phase === "search") {
    return (
      <OnboardingQuestion
        eyebrow="Your golf"
        title="Let’s find your JGS profile."
        description="I’ve filled in what I already know. Check the results and choose your profile."
        coachCopy="Your ranking lets me compare your scoring history with players recruited by each program."
        coachBadge="JGS"
      >
        <button
          type="button"
          onClick={() => setPhase("choice")}
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-extrabold text-muted-foreground transition-colors hover:text-forest"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Change my answer
        </button>
        <div className="rounded-2xl border border-border bg-white p-4 shadow-sm sm:p-5">
          <JgsSearchForm
            onLinkSuccess={handleLink}
            source="onboarding"
            initialName={profile.displayName ?? undefined}
            initialState={profile.state ?? undefined}
            initialGender={profile.gender ?? undefined}
            autoSearch
            onSkip={handleSkip}
          />
        </div>
      </OnboardingQuestion>
    );
  }

  return (
    <OnboardingQuestion
      eyebrow="Your golf"
      title="Do you have a Junior Golf Scoreboard ranking?"
      description="Choose the answer that sounds most like you."
      coachCopy="A ranking helps me judge which teams are realistic fits right now."
      coachBadge="★"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ChoiceCard
          selected={false}
          onClick={() => setPhase("search")}
          icon="JGS"
          title="Yes, find my profile"
          description="Search by my name and home state"
        />
        <ChoiceCard
          selected={false}
          onClick={handleSkip}
          icon="—"
          title="No, not yet"
          description="I haven’t played ranked events yet"
        />
        <ChoiceCard
          selected={false}
          onClick={() => setPhase("search")}
          icon="?"
          title="I’m not sure"
          description="Help me check before I move on"
        />
      </div>
    </OnboardingQuestion>
  );
}
